/**
 * One place that reports a failed discussion vote to the backend `FRONTEND` log stream.
 *
 * Why this module exists at all: both vote components already called
 * `logger.error("Agreement update failed", e)`, and that already forwarded to CloudWatch via
 * `lib/logger` -> `clientLogger.forwardStructuredLog` -> `AjaxClientLogController`. But each
 * component returned early on 401, 403 and 5xx BEFORE reaching it, so the only vote failures ever
 * logged were network and unclassified ones. The session-loss branch — the one that reloads the page
 * and therefore looks exactly like "I voted and my vote wasn't there" — was guaranteed never to log.
 * That is why the 2026-08-06 vote-persistence audit had to be answered with a prod database read.
 *
 * Deliberately NOT a GA4 event: GA4 is blocked for an unknown share of users, identifies them only by
 * a pseudonymous client id, cannot carry a CloudWatch metric filter or alarm, and lives behind the
 * business_plan/marketing seam. The client-log endpoint is first-party and already attaches the
 * SERVER-derived username, the page URL, and a `clientLogType` MDC key taken from `extra.type`.
 */
import logger from "@/lib/logger";

/** Value of `extra.type`, which `AjaxClientLogController` promotes to the `clientLogType` MDC key. */
export const VOTE_FAILURE_LOG_TYPE = "vote_failure";

export type DiscussionVoteFailureReason = "auth_session" | "forbidden" | "server_error" | "unknown";

export function classifyDiscussionVoteFailure(status: number | null | undefined,
                                              handledAuthSessionError: boolean): DiscussionVoteFailureReason {
    if (handledAuthSessionError) {
        return "auth_session";
    }
    if (status === 403) {
        return "forbidden";
    }
    if (typeof status === "number" && status >= 500) {
        return "server_error";
    }
    return "unknown";
}

export function logDiscussionVoteFailure(params: {
    discussionPublicId: string;
    mode: "AGREE" | "DISAGREE";
    operation: "add" | "remove";
    reason: DiscussionVoteFailureReason;
    status?: number | null;
    error?: unknown;
}) {
    const {discussionPublicId, mode, operation, reason, status, error} = params;

    // The 30 s dedupe in lib/logger keys on the STRING arguments only — `normalizeArgs` routes object
    // arguments to `obj`, which never reaches `shouldDeduplicate`. A constant message would therefore
    // collapse a burst of failures into ONE forwarded log per 30 seconds and silently drop the rest,
    // which is precisely backwards for the case worth seeing. The discussion id and the reason belong
    // in the message string for that reason, not only in `extra`.
    const message = `Agreement update failed [${discussionPublicId}] reason=${reason} op=${operation}`;
    const extra = {
        type: VOTE_FAILURE_LOG_TYPE,
        reason,
        operation,
        mode,
        discussionPublicId,
        status: typeof status === "number" ? status : null,
        err: error instanceof Error ? error : undefined,
    };

    // A 403 on a genuinely restricted discussion is CORRECT behavior, and a lost session is a fact of
    // life rather than a defect — error level for either would manufacture noise in a stream whose
    // value depends on staying quiet. `FORWARD_WARN` is true, so warn still reaches CloudWatch.
    if (reason === "server_error" || reason === "unknown") {
        logger.error(message, extra);
    } else {
        logger.warn(message, extra);
    }
}
